import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { Metamodel } from 'app/model/base/model';

import { CopySourceAdapter, commonCopySourceAdapts } from './copysource.adapter';
import { CopyActivity } from 'app/model/pipeline/copy.activity';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import { AzureDataExplorerCopySource } from 'app/model/pipeline/copysource/azure-data-explorer.copysource';
import { ParameterConfig } from 'app/model/pipeline/parameter-config';
import { Owned } from 'app/model/base/named';
import { IModel } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(AzureDataExplorerCopySource),
    asynchronous: true,
    adapts: {
        ...commonCopySourceAdapts,
        query: '*',
        queryTimeout: '*',
        noTruncation: '*',
        function: '*',
        functionParameters: '*'
    }
})
export class AzureDataExplorerCopySourceAdapter extends CopySourceAdapter<
    AzureDataExplorerCopySource,
    Model.AzureDataExplorerSource
> {
    protected model(): IModel {
        return AzureDataExplorerCopySource.MODEL;
    }

    protected override async _toResource(
        copySource: AzureDataExplorerCopySource
    ): Promise<Model.AzureDataExplorerSource> {
        const result: Model.AzureDataExplorerSource = this.getCommonCopySourceProperties(copySource, this.model().name);
        if (copySource.mode === 'function') {
            result.function = ValueAdapter.toResource(copySource, AzureDataExplorerCopySource.FUNCTION_NAME);
            const functionParameters: { [propertyName: string]: Model.StoredProcedureParameter } = {};
            copySource.functionParameters?.forEach((parameter) => {
                functionParameters[parameter.name] = {
                    type: parameter.type,
                    value: ValueAdapter.toResource(parameter, ParameterConfig.VALUE)
                };
            });
            if (Object.keys(functionParameters).length) {
                result.functionParameters = functionParameters;
            }
        } else {
            const query = ValueAdapter.toResource(copySource, AzureDataExplorerCopySource.QUERY);
            if (query != null && query !== '') {
                result.query = query;
            }
        }
        const queryTimeout = ValueAdapter.toResource(copySource, AzureDataExplorerCopySource.QUERY_TIMEOUT);
        if (queryTimeout != null && queryTimeout !== '') {
            // don't need to pass '' to backend
            result.queryTimeout = queryTimeout;
        }
        result.noTruncation = ValueAdapter.toResource(copySource, AzureDataExplorerCopySource.NO_TRUNCATION);
        return result;
    }

    protected override async _toModel(
        resource: Model.AzureDataExplorerSource,
        copyActivity: CopyActivity
    ): Promise<AzureDataExplorerCopySource> {
        const result = this.createCopySourceWithCommonProperties(
            this.model(),
            resource,
            copyActivity
        ) as AzureDataExplorerCopySource;
        if (resource.function != null) {
            result.mode = 'function';
            ValueAdapter.toModel(result, AzureDataExplorerCopySource.FUNCTION_NAME, resource.function);
            const functionParameters = resource.functionParameters;
            if (functionParameters != null) {
                result.functionParameters = Object.keys(functionParameters).map((name) => {
                    const parameter = Owned.create<ParameterConfig>(ParameterConfig.MODEL, copyActivity);
                    parameter.name = name;
                    parameter.type = functionParameters[name]?.type;
                    ValueAdapter.toModel(parameter, ParameterConfig.VALUE, functionParameters[name]?.value);
                    return parameter;
                });
            }
        }
        ValueAdapter.toModel(result, AzureDataExplorerCopySource.QUERY, resource.query);
        ValueAdapter.toModel(result, AzureDataExplorerCopySource.QUERY_TIMEOUT, resource.queryTimeout);
        ValueAdapter.toModel(result, AzureDataExplorerCopySource.NO_TRUNCATION, resource.noTruncation);
        return result;
    }
}
